import { prepareAccessToken, prepareAccountId, prepareMarketingData } from "@api/utils";
import { appConfig } from "@configs/application";
import i18n from "@configs/i18n";
import { API_ERROR_TYPE, captureApiException } from "@configs/sentry/utils";
import { ACCESS_TOKEN_ENDPOINT, DEFAULT_CONNECTION_ERROR_MESSAGE, Paths } from "@constants";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query";
import { RootState } from "@stores";
import auth from "@stores/auth";
import { persistCredential } from "@utils/credentials";
import { isTurtleModeForbiddenError } from "@utils/error-resolvers";
import { isTokenExpired } from "@utils/jwt";
import logger from "@utils/logger";
import { transformAccessToken } from "kz-ui-sdk";
import moment from "moment";
import toast from "react-hot-toast";

const BASE_URL = appConfig.server.baseURL;

// Empty response won't trigger error handling
// Use this to prevent error handling if we already handle the error in QueryFn
const EMPTY_RESPONSE = {
  data: {},
};

interface QueueItem {
  resolve: () => void;
  reject: (reason?: any) => void;
}

type QueryFn = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>;

let refreshQueue: QueueItem[] = [];
let isRefreshing = false;

function handleQueue(err: Error | null) {
  refreshQueue.forEach((prom) => {
    if (err) {
      prom.reject(err);
    } else {
      prom.resolve();
    }
  });
  isRefreshing = false;
  refreshQueue = [];
}

/**
 * Query with internet connection handling
 * @description Checks if there is an active internet connection before making API requests.
 * If no connection is available, it logs an error and shows a toast notification.
 */
export const withResponseInterceptors =
  (queryFn: QueryFn): QueryFn =>
  async (args, api, extraOptions) => {
    try {
      const result = await queryFn(args, api, extraOptions);

      // Check for network errors
      if (result.error?.status === "FETCH_ERROR") {
        // offline
        if (!navigator.onLine) {
          logger._console.log("Network error detected");
          toast.error(i18n.t("no internet connection. please try again."), {
            id: "network-error",
          });
  
          return EMPTY_RESPONSE;
        }
        // server error
        if (result.error?.error === "TypeError: Failed to fetch") {
          // ignore maintenance page error
          if (window?.location?.pathname?.includes(Paths.PUBLIC.MAINTENANCE)) {
            return EMPTY_RESPONSE;
          }
          toast.error(i18n.t(DEFAULT_CONNECTION_ERROR_MESSAGE), {
            id: "network-error",
          });

          return EMPTY_RESPONSE;
        }
      }

      // persist credential if result is success
      if (api.endpoint === "login" && !result.error) {
        const grantType = (args as FetchArgs).body.grant_type;
        switch (grantType) {
          case "password": {
            const { client_id, client_secret } = (args as FetchArgs).body;
            persistCredential({ clientId: client_id, password: client_secret, loginMethod: "username" });
            break;
          }
          case "otp": {
            const { phone } = (args as FetchArgs).body;
            persistCredential({ phone, loginMethod: "phone" });
            break;
          }
        }
      }

      // Capture API exception to Sentry
      if (result.error) {
        captureApiException(args as FetchArgs, result.error, API_ERROR_TYPE.API_ERROR);
      }

      return result;
    } catch (error) {
      if (!navigator.onLine) {
        logger._console.log("Network error detected during API call", error);
        toast.error(i18n.t("no internet connection. please try again."), {
          id: "network-error",
        });
        return {
          error: { status: "FETCH_ERROR", error: "No internet connection" } as FetchBaseQueryError,
        };
      }
      throw error;
    }
  };

/**
 * Query with auth header
 * @description Handle auth header and marketing data
 */
export const queryWithAuth = withResponseInterceptors(
  fetchBaseQuery({
    baseUrl: BASE_URL,
    timeout: 1000 * 60,
    prepareHeaders: (headers, api) => {
      const state = api.getState() as RootState;
      prepareAccessToken(headers, api, state);
      prepareMarketingData(headers, api);
      prepareAccountId(headers);
    },
  }),
);

/**
 * Query with auth header and refresh token
 * @description Handle auth header, auto refresh token when expired
 */
export const queryWithRefresh: QueryFn = async (args, api, extraOptions) => {
  let shouldRefreshToken = false;
  const state = api.getState() as RootState;
  const accessToken = state.auth.oauth?.access_token;

  if (isRefreshing) {
    logger._console.log("Waiting for refresh token");
    return new Promise<void>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    })
      .then(() => {
        logger._console.log("Refresh token success, re-call original api");
        return queryWithRefresh(args, api, extraOptions);
      })
      .catch((error) => {
        logger._console.log("Error while waiting for refresh token", error);
        return Promise.reject(error);
      });
  }

  if (
    // When login with password, we don't need to refresh token
    ((args as FetchArgs).url === ACCESS_TOKEN_ENDPOINT && (args as FetchArgs).body.grant_type === "password") ||
    // When call to public endpoint, we don't need to refresh token
    !accessToken
  ) {
    // Return original api call
    return queryWithAuth(args, api, extraOptions);
  }

  let result;
  // Check if token is expired
  if (isTokenExpired(accessToken)) {
    logger._console.log("Access token expired, skip request");
    shouldRefreshToken = true;
  } else {
    // If token is not expired, call original api
    result = await queryWithAuth(args, api, extraOptions);
  }

  // If token expired or api call return 401, try to refresh token
  if (shouldRefreshToken || (result && result.error && result.error.status === 401)) {
    // Prevent multiple refresh token call
    isRefreshing = true;
    logger._console.log("Start refresh token");
    const state = api.getState() as RootState;
    let tokenReloadSuccess = false;
    api.dispatch(
      auth.slice.actions.progressRefreshAccessToken({
        isRefreshingToken: true,
      }),
    );
    // Check if refresh token is available and not expired
    if (state.auth.oauth?.refresh_token && moment.unix(state.auth.oauth?.refresh_expires_at ?? 0).isAfter(moment())) {
      const refreshToken = state.auth.oauth.refresh_token;
      const refreshResult = await queryWithAuth(
        {
          method: "POST",
          url: ACCESS_TOKEN_ENDPOINT,
          body: {
            grant_type: "refresh_token",
            refresh_token: refreshToken,
          },
        },
        api,
        extraOptions,
      );
      if (refreshResult.data) {
        logger._console.log("Refresh token api call success, re-call original api");
        const authToken = transformAccessToken(refreshResult.data);
        api.dispatch(auth.slice.actions.updateAccessToken(authToken));
        handleQueue(null);
        // Refresh token success, re-call original api
        result = await queryWithAuth(args, api, extraOptions);
        tokenReloadSuccess = true;
      }
    }
    // If refresh token failed, logout user
    if (!tokenReloadSuccess) {
      if (state.auth.oauth?.refresh_token) {
        logger._console.log("Refresh token expired");
        toast.error(i18n.t("Your session has expired, please log in again!"), {
          id: "session-expired",
        });
      }
      handleQueue(new Error("Failed to refresh token"));
      api.dispatch(auth.slice.actions.logout());
    }

    api.dispatch(
      auth.slice.actions.progressRefreshAccessToken({
        isRefreshingToken: false,
      }),
    );
  }

  // Account suspension / auth 403: not the same as business `ForbiddenError` (e.g. deposit turtle `try again later`).
  if (result && result.error && result.error.status === 403) {
    if (isTurtleModeForbiddenError(result.error)) {
      return result;
    }
    logger._console.log("user is suspended");
    const text = i18n.t("Please contact customer service!");
    api.dispatch(auth.slice.actions.logout());
    handleQueue(new Error(text));
    toast.error(text, {
      id: "suspended-user",
    });
  }

  return (
    result ?? {
      error: {
        status: 401,
        data: "Refresh token expired",
      },
    }
  );
};
